import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import SearchableListbox from '../components/SearchableListbox';
import PendienteDialog from '../components/PendienteDialog';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import AsignadosBubbles from '../components/AsignadoBubble';
import PendienteNotas from '../components/PendienteNotas';
import PendienteRecordarDialog from '../components/PendienteRecordarDialog';
import { useUser } from '../components/UserContext';
import { useClients, useContracts } from '../contexts/DataContext';
import { ContratoView } from '../components/ContratoView';
import { FianzaView } from '../components/FianzaView';
import { usePendientesDecorados } from '../hooks/usePendientes';
import usePulsoData from '../hooks/usePulsoData';
import { logBitacora } from '../utils/bitacora';
import { mutateFianza } from '../utils/contractWrites';
import { cancelarEndosoPendiente, asignarEndosoPendiente } from '../utils/fianzaEventos';
import {
  PENDIENTE_TIPOS,
  PENDIENTE_TIPO_LABELS,
  PENDIENTE_ESTATUS,
  PENDIENTE_ESTATUS_LABELS,
  contarPendientes,
  ordenarPorUrgencia,
  pendienteFromEndoso,
  buildResolucion,
  isAbierto,
  agruparCompromisosPorAfianzadora,
  esDeLaOrg,
  ORG_LABEL,
  estaAsignado,
  normalizarAsignados,
  buildAsignacion,
  buildCancelacion,
} from '../utils/pendientes';
import {
  EnvelopeIcon, CheckCircleIcon, XMarkIcon, PencilSquareIcon,
  ClipboardDocumentListIcon, ChatBubbleLeftEllipsisIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

// Like Fianzas' POR_EMITIR tile, this one does not filter the table — it swaps
// it for the per-afianzadora rollup, because a compromiso is not a row about a
// fiado, it is an obligation with a carrier.
const COMPROMISOS = 'compromisos';
// Closed rows (resueltos + cancelados), newest closure first. Fran: "ver
// pendientes pasados estaría bien" — the estatus select could already show
// them, but a filter nobody finds is not a feature.
const HISTORIAL = 'historial';
const HISTORIAL_DIAS = 30;

const TILES = [
  { key: 'vencidos', label: 'Vencidos', color: 'text-red-600' },
  { key: 'urgentes', label: 'Esta semana', color: 'text-amber-600' },
  { key: 'proximos', label: 'Próximos 30 días', color: 'text-cian-digital' },
  { key: 'sinFecha', label: 'Sin fecha', color: 'text-zinc-500' },
  { key: 'total', label: 'Abiertos', color: 'text-azul-surelia' },
  { key: COMPROMISOS, label: 'Compromisos', color: 'text-red-600' },
  { key: HISTORIAL, label: `Cerrados (${HISTORIAL_DIAS} días)`, color: 'text-zinc-500' },
];

const TILE_PREDICATES = {
  vencidos: (p) => p.urgencia === 'vencido',
  urgentes: (p) => p.urgencia === 'urgente',
  proximos: (p) => p.urgencia === 'proximo',
  sinFecha: (p) => p.urgencia === 'sinFecha',
  total: () => true,
  // No predicate for COMPROMISOS on purpose — both the count and the table
  // branch on it before the predicates run.
};

const URGENCIA_STYLES = {
  vencido: 'bg-red-50 text-red-700 border-red-200',
  urgente: 'bg-amber-50 text-amber-700 border-amber-200',
  proximo: 'bg-cian-digital/10 text-azul-surelia border-cian-digital/30',
  normal: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  sinFecha: 'bg-zinc-50 text-zinc-500 border-zinc-200',
};

const formatMonto = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('es-MX', {
    style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
  });
};

// When a closed row was closed: resueltoAt for resueltos, updatedAt for
// cancelados (cancelar only stamps updatedAt).
const fechaCierre = (p) => Number(p.resueltoAt || p.updatedAt || p.createdAt || 0);

const formatFecha = (ms) => {
  if (!ms) return 'Sin fecha';
  return new Date(Number(ms)).toLocaleDateString('es-MX', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const urgenciaLabel = (row) => {
  if (row.urgencia === 'sinFecha') return 'Sin fecha';
  const d = row.diasRestantes;
  if (d === null || d === undefined) return 'Sin fecha';
  if (d < 0) return `Venció hace ${Math.abs(d)} d`;
  if (d === 0) return 'Vence hoy';
  return `En ${d} d`;
};

const normalizar = (s) =>
  String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Pendientes — the cross-cartera chase list.
 *
 * Shows the `pendientes` collection plus the legacy endosos pendientes that
 * still live on the fianza (normalized through pendienteFromEndoso). Endoso
 * rows are read-only here: they are edited where the atomic
 * resolve-and-apply-the-endoso write lives, on the fianza.
 */
const Pendientes = () => {
  const navigate = useNavigate();
  const { user, userData } = useUser();
  const { miembros, nombreDe } = useOrgMiembros();
  const { rows: pendientesRows, loading } = usePendientesDecorados();
  const { contractsById } = useContracts();
  const { clientsById } = useClients();
  const { endososPendientes } = usePulsoData();

  const [selectedTile, setSelectedTile] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');
  const [filtroAsignado, setFiltroAsignado] = useState('all'); // all | mios | uid
  const [filtroEstatus, setFiltroEstatus] = useState('abiertos');
  const [busqueda, setBusqueda] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [recordarTarget, setRecordarTarget] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contratoAbierto, setContratoAbierto] = useState(null);
  const [fianzaAbierta, setFianzaAbierta] = useState(null);

  // Legacy endoso rows, normalized into the same shape. They are always open —
  // getEndososAbiertos already filtered the closed ones out upstream.
  const endosoRows = useMemo(
    () => (endososPendientes || []).map(pendienteFromEndoso),
    [endososPendientes]
  );

  const todas = useMemo(
    () => [...(pendientesRows || []), ...endosoRows],
    [pendientesRows, endosoRows]
  );

  const abiertas = useMemo(() => todas.filter(isAbierto), [todas]);

  const counts = useMemo(() => {
    const base = contarPendientes(abiertas);
    base[COMPROMISOS] = abiertas.filter(
      (p) => p.tipo === PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA
    ).length;
    const desde = Date.now() - HISTORIAL_DIAS * 86400000;
    base[HISTORIAL] = todas.filter(
      (p) => p.origen !== 'endoso' && !isAbierto(p) && fechaCierre(p) >= desde
    ).length;
    return base;
  }, [abiertas, todas]);

  const modoCompromisos = selectedTile === COMPROMISOS;
  const modoHistorial = selectedTile === HISTORIAL;

  // Monto afianzado of the fianza a compromiso hangs on. Resolved from the
  // contracts cache rather than inside the model, which stays Firestore-free.
  const montoDeFianza = useCallback((row) => {
    if (!row.contratoId || !row.fianzaId) return null;
    const contrato = contractsById[row.contratoId];
    const fianza = (contrato?.fianzas || []).find((f) => f.id === row.fianzaId);
    const monto = Number(fianza?.montoAfianzado);
    return Number.isFinite(monto) ? monto : null;
  }, [contractsById]);

  const grupos = useMemo(
    () => (modoCompromisos
      ? agruparCompromisosPorAfianzadora(abiertas, { montoDeFianza })
      : []),
    [modoCompromisos, abiertas, montoDeFianza]
  );

  const clienteOptions = useMemo(() => {
    const seen = new Map();
    let hayDeLaOrg = false;
    todas.forEach((p) => {
      if (esDeLaOrg(p)) { hayDeLaOrg = true; return; }
      const id = p.convenioId || p.clienteId;
      if (id && !seen.has(id)) seen.set(id, p.clientName || 'Sin nombre');
    });
    return [
      { value: 'all', label: 'Todos' },
      // The despacho's own to-dos sit first: they are not a fiado and would
      // otherwise get lost alphabetically among them.
      ...(hayDeLaOrg ? [{ value: 'org', label: ORG_LABEL }] : []),
      ...[...seen.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    ];
  }, [todas]);

  const filas = useMemo(() => {
    // Estatus first: the tiles are defined over open rows, so applying a tile
    // to a "resueltos" view would always come back empty.
    let out;
    if (modoHistorial) {
      // Closed only; the estatus select narrows to resueltos or cancelados.
      out = todas.filter((p) => p.origen !== 'endoso' && !isAbierto(p));
      if (filtroEstatus === PENDIENTE_ESTATUS.RESUELTO || filtroEstatus === PENDIENTE_ESTATUS.CANCELADO) {
        out = out.filter((p) => p.estatus === filtroEstatus);
      }
    } else {
      out = filtroEstatus === 'abiertos'
        ? abiertas
        : filtroEstatus === 'all'
          ? todas
          : todas.filter((p) => p.estatus === filtroEstatus);
      if (selectedTile && selectedTile !== COMPROMISOS && filtroEstatus === 'abiertos') {
        out = out.filter(TILE_PREDICATES[selectedTile]);
      }
    }
    if (filtroCliente === 'org') {
      out = out.filter(esDeLaOrg);
    } else if (filtroCliente !== 'all') {
      out = out.filter((p) => (p.convenioId || p.clienteId) === filtroCliente);
    }
    if (filtroTipo !== 'all') out = out.filter((p) => p.tipo === filtroTipo);
    if (filtroAsignado === 'mios') {
      out = out.filter((p) => estaAsignado(p, user?.uid));
    } else if (filtroAsignado !== 'all') {
      out = out.filter((p) => estaAsignado(p, filtroAsignado));
    }
    if (busqueda.trim()) {
      const q = normalizar(busqueda);
      out = out.filter((p) =>
        normalizar(p.titulo).includes(q) ||
        normalizar(p.detalle).includes(q) ||
        normalizar(p.clientName).includes(q) ||
        normalizar(p.contractTitle).includes(q)
      );
    }
    return modoHistorial
      ? [...out].sort((a, b) => fechaCierre(b) - fechaCierre(a))
      : ordenarPorUrgencia([...out]);
  }, [todas, abiertas, filtroEstatus, selectedTile, modoHistorial, filtroCliente, filtroTipo,
    filtroAsignado, user?.uid, busqueda]);


  // Pendientes de la org (sin relación) se registran sobre el doc de la org,
  // como hacen la burbuja y reasignar; sin documentId logBitacora no escribe.
  const bitacoraFor = (row, action, description) => ({
    action,
    collection: row.convenioId ? 'convenios' : row.clienteId ? 'clientes' : 'orgs',
    documentId: row.convenioId || row.clienteId || userData?.org,
    user,
    userData,
    metadata: {
      description,
      clientId: row.clienteId || null,
      convenioId: row.convenioId || null,
      contratoId: row.contratoId || null,
    },
  });

  const resolver = async (row) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'pendientes', row.id), buildResolucion({
        resueltoCon: { tipo: 'manual' },
        user,
        nombre: userData?.nombre || userData?.correo || null,
      }));
      await logBitacora(bitacoraFor(row, 'UPDATE',
        `Resolvió pendiente: ${row.titulo}`));
    } catch (e) {
      console.error('resolver pendiente failed', e);
    } finally {
      setBusy(false);
    }
  };

  const cancelar = async (row) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'pendientes', row.id),
        buildCancelacion({ user, nombre: userData?.nombre || userData?.correo || null }));
      await logBitacora(bitacoraFor(row, 'UPDATE',
        `Canceló pendiente: ${row.titulo}`));
    } catch (e) {
      console.error('cancelar pendiente failed', e);
    } finally {
      setBusy(false);
    }
  };

  // Where a row gets RESOLVED, not merely where it is filed. An endoso lives on
  // the fianza and is closed by registering it there, so the row has to open
  // FianzaView — navigating to the cliente page left the user to hunt for it.
  // (The first version read row.clienteId on endoso rows, which carry clientId
  // from usePulsoData, so both branches missed and the button did nothing.)
  // Reassign in place from the avatar bubble.
  const [reasignando, setReasignando] = useState(null);
  const reasignar = async (row, uids) => {
    setReasignando(row.id);
    try {
      await updateDoc(doc(db, 'pendientes', row.id), buildAsignacion(
        uids, null, user?.uid,
        { uid: user?.uid, nombre: userData?.nombre || userData?.correo || null },
        uids.map((u) => (u === user?.uid ? 'sí mismo' : (nombreDe(u) || u))).join(', ') || 'nadie',
      ));
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: row.convenioId ? 'convenios' : row.clienteId ? 'clientes' : 'orgs',
          documentId: row.convenioId || row.clienteId || userData.org,
          user,
          userData,
          metadata: {
            description: uids.length
              ? `Asignó pendiente "${row.titulo}" a ${uids.map((u) => (u === user?.uid ? 'sí mismo' : (nombreDe(u) || u))).join(', ')}`
              : `Dejó sin asignar el pendiente "${row.titulo}"`,
            clientId: row.clienteId || null,
          },
        });
      } catch (e) { console.error('bitacora reasignar failed', e); }
    } catch (e) {
      console.error('reasignar pendiente failed', e);
    } finally {
      setReasignando(null);
    }
  };

  // An endoso request lives on the fianza, so its assignees are written into
  // the request object through the same contract write as everything else.
  const reasignarEndoso = async (row, uids) => {
    const contratoId = row.contractId || row.contratoId;
    if (!contratoId || !row.fianzaId || !row.endosoReqId) return;
    setReasignando(row.id);
    try {
      const ts = Date.now();
      await mutateFianza(db, contratoId, row.fianzaId, (current) => ({
        ...asignarEndosoPendiente(current, row.endosoReqId, uids, ts),
        updatedAt: ts,
      }));
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoId,
          user,
          userData,
          metadata: {
            contratoId,
            fianzaId: row.fianzaId,
            endosoPendienteId: row.endosoReqId,
            description: uids.length
              ? `Asignó endoso pendiente de fianza #${row.numeroFianza || 'sin número'} a ${uids.map((u) => (u === user?.uid ? 'sí mismo' : (nombreDe(u) || u))).join(', ')}`
              : `Dejó sin asignar el endoso pendiente de fianza #${row.numeroFianza || 'sin número'}`,
          },
        });
      } catch (e) { console.error('bitacora reasignar endoso failed', e); }
    } catch (e) {
      console.error('reasignar endoso failed', e);
    } finally {
      setReasignando(null);
    }
  };

  // An endoso request lives on the fianza; closing it is a contract write.
  const [busyEndoso, setBusyEndoso] = useState(null);
  const cancelarSolicitudEndoso = async (row) => {
    const contratoId = row.contractId || row.contratoId;
    if (!contratoId || !row.fianzaId || !row.endosoReqId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('¿Cancelar esta solicitud de endoso?')) return;
    setBusyEndoso(row.id);
    try {
      const ts = Date.now();
      await mutateFianza(db, contratoId, row.fianzaId, (current) => ({
        ...cancelarEndosoPendiente(current, row.endosoReqId, null, ts),
        updatedAt: ts,
      }));
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoId,
          user,
          userData,
          metadata: {
            contratoId,
            fianzaId: row.fianzaId,
            endosoPendienteId: row.endosoReqId,
            description: `Canceló solicitud de endoso en fianza #${row.numeroFianza || 'sin número'}`,
          },
        });
      } catch (e) {
        console.error('bitacora cancelar endoso pendiente failed', e);
      }
    } catch (e) {
      console.error('cancelar solicitud endoso failed', e);
    } finally {
      setBusyEndoso(null);
    }
  };

  // The name in "Relacionado con" is a link to the fiado (or convenio)
  // itself; opening the contrato/fianza where the row gets resolved is the
  // separate "Abrir" action.
  const irAlRelacionado = (row) => {
    if (row.convenioId) return navigate(`/app/convenios/${row.convenioId}`);
    if (row.clienteId) return navigate(`/app/clientes/${row.clienteId}`);
    return undefined;
  };

  const abrirEnContexto = (row) => {
    const contrato = row.contratoId ? contractsById[row.contratoId] : null;
    if (contrato && row.fianzaId) {
      const fianza = (contrato.fianzas || []).find((f) => f.id === row.fianzaId);
      if (fianza) {
        return setFianzaAbierta({
          ...fianza,
          contratoId: contrato.id,
          clientId: contrato.clientId,
        });
      }
    }
    if (contrato) return setContratoAbierto(contrato);
    if (row.convenioId) return navigate(`/app/convenios/${row.convenioId}`);
    if (row.clienteId) return navigate(`/app/clientes/${row.clienteId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Heading level={1} className="text-azul-surelia mb-1">Pendientes</Heading>
          <Text className="text-zinc-600">Lo que falta entregar y quién lo debe</Text>
        </div>
        <div className="flex items-center gap-2">
          <Button color="surelia" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Nuevo pendiente
          </Button>
        </div>
      </div>

      {/* Seven tiles: one row on desktop, never a lone tile wrapping. */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {TILES.map(({ key, label, color }) => {
          const active = selectedTile === key;
          return (
            <button
              key={key}
              onClick={() => {
                setSelectedTile(active ? null : key);
                // Historial is the one tile whose rows are not open.
                setFiltroEstatus(!active && key === HISTORIAL ? 'all' : 'abiertos');
              }}
              className={`text-left bg-white rounded-lg border p-3 shadow-sm transition-all duration-200 hover:shadow-md ${
                active
                  ? 'border-azul-surelia ring-2 ring-azul-surelia/30'
                  : 'border-zinc-200 hover:border-azul-surelia/40'
              }`}
            >
              <div className={`text-2xl font-bold tabular-nums ${counts[key] > 0 ? color : 'text-zinc-300'}`}>
                {counts[key]}
              </div>
              <Text className="text-xs text-zinc-600">{label}</Text>
            </button>
          );
        })}
      </div>

      {!modoCompromisos && (
      <div className="mb-4 bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-center">
          <div className="flex items-center gap-2 min-w-[220px]">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Relacionado con:</label>
            <SearchableListbox
              value={filtroCliente}
              onChange={setFiltroCliente}
              options={clienteOptions}
              className="flex-1"
              popupClassName="w-64"
            />
          </div>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          >
            <option value="all">Todos los tipos</option>
            {Object.values(PENDIENTE_TIPOS).map((t) => (
              <option key={t} value={t}>{PENDIENTE_TIPO_LABELS[t]}</option>
            ))}
          </select>
          <select
            value={filtroAsignado}
            onChange={(e) => setFiltroAsignado(e.target.value)}
            className="rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          >
            <option value="all">Cualquier asignado</option>
            <option value="mios">Míos</option>
            {miembros.filter((m) => m.id !== user?.uid).map((m) => (
              <option key={m.id} value={m.id}>{m.nombre || m.correo}</option>
            ))}
          </select>
          <select
            value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setSelectedTile(null); }}
            className="rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          >
            {modoHistorial ? (
              <>
                <option value="all">Resueltos y cancelados</option>
                <option value={PENDIENTE_ESTATUS.RESUELTO}>Resueltos</option>
                <option value={PENDIENTE_ESTATUS.CANCELADO}>Cancelados</option>
              </>
            ) : (
              <>
                <option value="abiertos">Abiertos</option>
                <option value={PENDIENTE_ESTATUS.RESUELTO}>Resueltos</option>
                <option value={PENDIENTE_ESTATUS.CANCELADO}>Cancelados</option>
                <option value="all">Todos</option>
              </>
            )}
          </select>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar"
            className="flex-1 min-w-[160px] rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          />
        </div>
      </div>
      )}

      {modoCompromisos ? (
        grupos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
            <ShieldExclamationIcon className="w-10 h-10" />
            <Text className="text-sm text-zinc-500">
              No hay compromisos abiertos con ninguna afianzadora.
            </Text>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                  <th className="px-3 py-2">Afianzadora</th>
                  <th className="px-3 py-2 text-right">Abiertos</th>
                  <th className="px-3 py-2 text-right">Vencidos</th>
                  <th className="px-3 py-2 text-right">Fiados</th>
                  <th className="px-3 py-2 text-right">Más viejo</th>
                  <th className="px-3 py-2 text-right">Monto expuesto</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {grupos.map((g) => {
                  const abierto = expandido === `grupo:${g.afianzadora}`;
                  return (
                    <React.Fragment key={g.afianzadora}>
                      <tr className={g.vencidos > 0 ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-2.5 font-medium text-zinc-800">
                          {g.afianzadora}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-700">
                          {g.total}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                          g.vencidos > 0 ? 'text-red-700' : 'text-zinc-300'
                        }`}>
                          {g.vencidos}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-600">
                          {g.fiados}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-600">
                          {g.masViejoDias} d
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-700">
                          {formatMonto(g.montoExpuesto)}
                          {/* The figure understates reality when a compromiso
                              is not tied to a specific fianza. Say so rather
                              than presenting a partial sum as complete. */}
                          {g.montoParcial && (
                            <span className="ml-1 text-zinc-400" title="Algunos compromisos no están ligados a una fianza">
                              +
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            onClick={() => setExpandido(abierto ? null : `grupo:${g.afianzadora}`)}
                            className="text-xs text-azul-surelia hover:underline"
                          >
                            {abierto ? 'Ocultar' : 'Ver'}
                          </button>
                        </td>
                      </tr>
                      {abierto && g.rows.map((row) => (
                        <tr key={row.id} className="bg-zinc-50/60">
                          <td className="px-3 py-2 pl-8" colSpan={3}>
                            <button
                              onClick={() => abrirEnContexto(row)}
                              className="text-left"
                            >
                              <div className="text-sm text-zinc-800">{row.titulo}</div>
                              <div className="text-xs text-zinc-500">{row.clientName}</div>
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-zinc-500" colSpan={2}>
                            {urgenciaLabel(row)}
                          </td>
                          <td className="px-3 py-2 text-right text-xs tabular-nums text-zinc-600">
                            {formatMonto(montoDeFianza(row))}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {isAbierto(row) && (
                              <button
                                title="Recordar al responsable"
                                onClick={() => setRecordarTarget(row)}
                                className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                              >
                                <EnvelopeIcon className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : filas.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
          <ClipboardDocumentListIcon className="w-10 h-10" />
          <Text className="text-sm text-zinc-500">No hay pendientes con estos filtros.</Text>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2">Pendiente</th>
                <th className="px-3 py-2">Relacionado con</th>
                <th className="px-3 py-2">Asignado</th>
                <th className="px-3 py-2">{modoHistorial ? 'Cerrado' : 'Fecha límite'}</th>
                <th className="px-3 py-2">Estatus</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filas.map((row) => {
                const esEndoso = row.origen === 'endoso';
                const abierto = expandido === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => setExpandido(abierto ? null : row.id)}
                          className="text-left"
                        >
                          <div className="font-medium text-zinc-800 flex items-center gap-1.5">
                            {row.titulo}
                            {(row.notasInternas || []).length > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[11px] font-normal text-zinc-400">
                                <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />
                                {row.notasInternas.length}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {PENDIENTE_TIPO_LABELS[row.tipo] || row.tipo}
                            {esEndoso && ' — en la fianza'}
                          </div>
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        {esDeLaOrg(row) ? (
                          <span className="text-zinc-500">{row.clientName}</span>
                        ) : (
                          <button
                            onClick={() => irAlRelacionado(row)}
                            title={row.convenioId ? 'Ir al convenio' : 'Ir al fiado'}
                            className="text-azul-surelia hover:underline text-left"
                          >
                            {row.clientName}
                          </button>
                        )}
                        {/* What the name IS, since this column holds fiados,
                            convenios and the org alike. A fiado needs no
                            label — it is the common case. */}
                        {(row.convenioId || row.contractTitle) && (
                          <div className="text-xs text-zinc-400 truncate max-w-[200px]">
                            {row.convenioId ? 'Convenio' : ''}
                            {row.convenioId && row.contractTitle ? ' · ' : ''}
                            {row.contractTitle}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <AsignadosBubbles
                          uids={normalizarAsignados(row)}
                          miembros={miembros}
                          meUid={user?.uid}
                          disabled={!isAbierto(row) || reasignando === row.id}
                          onChange={(uids) => (esEndoso ? reasignarEndoso(row, uids) : reasignar(row, uids))}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        {modoHistorial ? (
                          <>
                            <div className="text-sm text-zinc-700">{formatFecha(fechaCierre(row))}</div>
                            <div className="text-xs text-zinc-400 mt-0.5">
                              {row.resueltoPor
                                ? (row.resueltoPor === user?.uid ? 'Yo' : nombreDe(row.resueltoPor) || '')
                                : ''}
                              {row.fechaLimite ? `${row.resueltoPor ? ' · ' : ''}límite ${formatFecha(row.fechaLimite)}` : ''}
                            </div>
                          </>
                        ) : (
                          <>
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${URGENCIA_STYLES[row.urgencia] || URGENCIA_STYLES.normal}`}>
                              {urgenciaLabel(row)}
                            </span>
                            <div className="text-xs text-zinc-400 mt-0.5">{formatFecha(row.fechaLimite)}</div>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600">
                        {PENDIENTE_ESTATUS_LABELS[row.estatus] || row.estatus}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {esEndoso ? (
                            <>
                              <Button plain onClick={() => abrirEnContexto(row)}>Abrir</Button>
                              {isAbierto(row) && (
                                <button
                                  title="Cancelar solicitud de endoso"
                                  disabled={busyEndoso === row.id}
                                  onClick={() => cancelarSolicitudEndoso(row)}
                                  className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              {row.contratoId && (
                                <Button plain onClick={() => abrirEnContexto(row)}>Abrir</Button>
                              )}
                              {isAbierto(row) && (
                                <button
                                  title="Recordar al responsable"
                                  onClick={() => setRecordarTarget(row)}
                                  className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                                >
                                  <EnvelopeIcon className="w-4 h-4" />
                                </button>
                              )}
                              {isAbierto(row) && (
                                <>
                                  <button
                                    title="Editar"
                                    onClick={() => { setEditing(row); setDialogOpen(true); }}
                                    className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                                  >
                                    <PencilSquareIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    title="Marcar resuelto"
                                    disabled={busy}
                                    onClick={() => resolver(row)}
                                    className="p-1.5 rounded hover:bg-green-50 text-green-600"
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    title="Cancelar"
                                    disabled={busy}
                                    onClick={() => cancelar(row)}
                                    className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {abierto && (
                      <tr className="bg-zinc-50/60">
                        <td colSpan={6} className="px-3 pb-4 pt-2">
                          <div className="max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm p-4">
                            {row.detalle && (
                              <Text className="text-sm text-gray-700 mb-3">{row.detalle}</Text>
                            )}
                            {!esEndoso && (
                              <>
                                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-3">
                                  <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-azul-surelia" />
                                  Actualizaciones
                                </h3>
                                <PendienteNotas pendiente={row} />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {contratoAbierto && (
        <ContratoView
          isOpen
          onClose={() => setContratoAbierto(null)}
          contrato={contratoAbierto}
          clientData={clientsById[contratoAbierto.clientId]}
        />
      )}

      {fianzaAbierta && (
        <FianzaView
          isOpen
          onClose={() => setFianzaAbierta(null)}
          fianza={fianzaAbierta}
          clientData={clientsById[fianzaAbierta.clientId]}
          contratoData={contractsById[fianzaAbierta.contratoId]}
        />
      )}

      <PendienteDialog
        isOpen={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        pendiente={editing}
      />

      {recordarTarget && (
        <PendienteRecordarDialog
          open
          onClose={() => setRecordarTarget(null)}
          pendiente={recordarTarget}
        />
      )}
    </div>
  );
};

export default Pendientes;
